"use client"

import {
  ComponentPropsWithoutRef,
  FormEvent,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import {
  Check,
  MessageSquare,
  RefreshCw,
  RotateCcw,
  Send,
  X,
} from "lucide-react"
import Link from "next/link"
import { usePathname, useSearchParams } from "next/navigation"
import type { FeedbackThread } from "@/lib/feedback/types"
import Tooltip from "@/ui/Tooltip"
import {
  type FeedbackReaction,
  FEEDBACK_REACTION_GLYPHS,
  FEEDBACK_REACTION_LABELS,
  FEEDBACK_REACTION_ORDER,
} from "./feedbackReactions"
import { SubmittedFeedbackLayer } from "./SubmittedFeedbackLayer"

type SelectionDraft = {
  selectedText: string
  selectionPrefix: string | null
  selectionSuffix: string | null
  left: number
  // Viewport span of the highlighted text; computePopoverTop derives
  // the popover's final top from it (and the measured dialog height).
  anchorTop: number
  anchorBottom: number
  width: number
}

// Vertical gap between the highlighted text and the popover.
const POPOVER_ANCHOR_GAP = 10

// Popover top: centered on the selection, clamped within the fold.
const computePopoverTop = (
  anchorTop: number,
  anchorBottom: number,
  height: number,
) => {
  const viewportHeight = window.innerHeight

  const selectionCenter = (anchorTop + anchorBottom) / 2
  const centeredTop = selectionCenter - height / 2

  const maxTop = viewportHeight - height - POPOVER_MARGIN
  if (maxTop < POPOVER_MARGIN) {
    return POPOVER_MARGIN
  }

  return Math.min(Math.max(centeredTop, POPOVER_MARGIN), maxTop)
}

// --- Submit animation ---------------------------------------------
// After a save the dialog fades out, then SubmittedFeedbackLayer's
// margin icon and the dotted underline take over.
const SUBMIT_FADE_DURATION_MS = 200

type Notice = {
  tone: "success" | "error"
  text: string
}

type PageInfo = {
  pagePath: string
  pageUrl: string
}

type FeedbackIdentity = {
  id: string
  username: string
}

type ThreadResponse = {
  thread?: FeedbackThread
  error?: string
}

type ThreadsResponse = {
  threads?: FeedbackThread[]
  error?: string
}

type LoadThreadsOptions = {
  showErrors?: boolean
}

type UsernameInputProps = {
  inputId: string
  username: string
  onUsernameChange: (username: string) => void
}

const MIN_SELECTED_TEXT_LENGTH = 5
const MAX_SELECTED_TEXT_LENGTH = 1200
const MAX_COMMENT_LENGTH = 4000
const MAX_CONTEXT_LENGTH = 1200
const POPOVER_MAX_WIDTH = 340
const POPOVER_MARGIN = 12
const MAX_USERNAME_LENGTH = 40
const FEEDBACK_IDENTITY_STORAGE_KEY = "ai2030.feedback.identity"
const FEEDBACK_LAST_OPENED_STORAGE_PREFIX = "ai2030.feedback.lastOpened"
const FEEDBACK_WIDGET_MEDIA_QUERY = "(min-width: 930px)"

// Cassandra access key set by /feedback; its presence unlocks the badge.
const FEEDBACK_ALL_THREADS_ACCESS_KEY_STORAGE_KEY =
  "ai2030.feedback.allThreadsAccessKey"

// Epoch-ms of the prior /feedback visit; the reviewer-badge cutoff.
const FEEDBACK_LAST_VISIT_STORAGE_KEY = "ai2030.feedback.lastVisitMs"

const REVIEWER_BADGE_POLL_INTERVAL_MS = 60_000

const MAX_SELECTION_PREVIEW_LENGTH = 45

const SELECTION_COMMENT_PLACEHOLDER = "Write a comment (optional)"

const SELECTION_REACT_LABEL = "Add a reaction (optional)"

const REPLIES_BADGE_TOOLTIP = "New replies to your feedback on this page"

const REVIEWER_BADGE_TOOLTIP =
  "New comments across the site since your last visit to /feedback"

// Badge link; params widen the feed's filters past its page defaults so
// counted comments (resolved/other-version) aren't hidden.
const REVIEWER_BADGE_HREF =
  "/feedback?feed=1&recency=sinceLastVisit&status=all&version=all"

const DIALOG_UI_FONT_FAMILY =
  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, ' +
  '"Liberation Mono", "Courier New", monospace'

const DIALOG_COPY_FONT_FAMILY =
  'et-book, Palatino, "Palatino Linotype", "Palatino LT STD", ' +
  '"Book Antiqua", Georgia, serif'

// Reaction labels/glyphs live in ./feedbackReactions; edit dicts there.

type SelectionStep = "comment" | "name"

const NAME_REQUIRED_TOOLTIP_CONTENT = (
  <div className="leading-snug">
    <div>Enter a name</div>
    <div className="mt-2 text-black/70">
      (it can be a pseudonym if you want to be anonymous, although identifiable
      names are helpful)
    </div>
  </div>
)

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max)

const createLocalId = () => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID()
  }

  return `local-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

const createFeedbackIdentity = (): FeedbackIdentity => ({
  id: createLocalId(),
  username: "",
})

const getStoredFeedbackIdentity = () => {
  const storedValue = window.localStorage.getItem(FEEDBACK_IDENTITY_STORAGE_KEY)
  if (!storedValue) return createFeedbackIdentity()

  try {
    const parsedValue = JSON.parse(storedValue) as Partial<FeedbackIdentity>
    if (!parsedValue.id) {
      return createFeedbackIdentity()
    }

    return {
      id: parsedValue.id.slice(0, 100),
      username: (parsedValue.username ?? "").slice(0, MAX_USERNAME_LENGTH),
    }
  } catch {
    return createFeedbackIdentity()
  }
}

const saveFeedbackIdentity = (identity: FeedbackIdentity) => {
  window.localStorage.setItem(
    FEEDBACK_IDENTITY_STORAGE_KEY,
    JSON.stringify(identity)
  )
}

const getLastOpenedStorageKey = (pagePath: string) =>
  `${FEEDBACK_LAST_OPENED_STORAGE_PREFIX}:${pagePath}`

const readStoredAllThreadsAccessKey = (): string | null => {
  try {
    const stored = window.localStorage.getItem(
      FEEDBACK_ALL_THREADS_ACCESS_KEY_STORAGE_KEY
    )
    return stored?.trim() || null
  } catch {
    return null
  }
}

const readStoredLastVisitMs = (): number | null => {
  try {
    const stored = window.localStorage.getItem(FEEDBACK_LAST_VISIT_STORAGE_KEY)
    if (!stored) return null
    const parsed = Number(stored)
    return Number.isFinite(parsed) ? parsed : null
  } catch {
    return null
  }
}

const getNodeElement = (node: Node | null) => {
  if (!node) return null
  if (node.nodeType === Node.ELEMENT_NODE) return node as Element
  return node.parentElement
}

const getNodePath = (node: Node | null) => {
  const path: number[] = []
  let currentNode = node

  while (currentNode?.parentNode && currentNode !== document.body) {
    const siblingIndex = Array.prototype.indexOf.call(
      currentNode.parentNode.childNodes,
      currentNode
    )
    path.unshift(siblingIndex)
    currentNode = currentNode.parentNode
  }

  return path.join(".")
}

const getSelectionSignature = (selection: Selection | null) => {
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
    return null
  }

  const selectedText = selection.toString().trim()
  if (!selectedText) return null

  const range = selection.getRangeAt(0)
  return [
    selectedText,
    getNodePath(range.startContainer),
    range.startOffset,
    getNodePath(range.endContainer),
    range.endOffset,
  ].join("|")
}

const isEditableElement = (element: Element | null) =>
  element instanceof HTMLInputElement ||
  element instanceof HTMLTextAreaElement ||
  element instanceof HTMLSelectElement ||
  element?.getAttribute("contenteditable") === "true"

const compactText = (text: string) => text.replace(/\s+/g, " ").trim()

const getSelectionPreview = (text: string) => {
  const compact = compactText(text)
  if (compact.length <= MAX_SELECTION_PREVIEW_LENGTH) {
    return { text: compact, truncated: false }
  }

  return {
    text: compact.slice(0, MAX_SELECTION_PREVIEW_LENGTH).trimEnd(),
    truncated: true,
  }
}

const getSelectionContext = (range: Range, selectedText: string) => {
  const containerText = compactText(range.commonAncestorContainer.textContent ?? "")
  const normalizedSelection = compactText(selectedText)
  const selectionIndex = containerText.indexOf(normalizedSelection)

  if (selectionIndex === -1) {
    return {
      selectionPrefix: null,
      selectionSuffix: null,
    }
  }

  return {
    selectionPrefix: containerText.slice(
      Math.max(0, selectionIndex - MAX_CONTEXT_LENGTH),
      selectionIndex
    ),
    selectionSuffix: containerText.slice(
      selectionIndex + normalizedSelection.length,
      selectionIndex + normalizedSelection.length + MAX_CONTEXT_LENGTH
    ),
  }
}

const getRangeRect = (range: Range) => {
  const rects = Array.from(range.getClientRects()).filter(
    rect => rect.width > 0 && rect.height > 0
  )

  return rects[rects.length - 1] ?? range.getBoundingClientRect()
}

// Custom Highlight API, so the highlight survives focus moving into the
// dialog (native selection clears once an input is focused).
const SELECTION_HIGHLIGHT_NAME = "ai2030-feedback-selection"

// Restyles ::selection to match the highlight (globals.css) so the still-
// alive native selection (kept so the reader can copy) renders as one.
const SELECTION_HIGHLIGHT_ACTIVE_CLASS = "ai2030-feedback-selection-active"

const supportsCustomHighlights = () =>
  typeof CSS !== "undefined" &&
  "highlights" in CSS &&
  typeof Highlight !== "undefined"

const setSelectionHighlight = (range: Range) => {
  if (!supportsCustomHighlights()) return

  try {
    CSS.highlights.set(SELECTION_HIGHLIGHT_NAME, new Highlight(range.cloneRange()))
    document.documentElement.classList.add(SELECTION_HIGHLIGHT_ACTIVE_CLASS)
  } catch {
    // Best-effort; the dialog works without the persistent highlight.
  }
}

const clearSelectionHighlight = () => {
  if (!supportsCustomHighlights()) return
  CSS.highlights.delete(SELECTION_HIGHLIGHT_NAME)
  document.documentElement.classList.remove(SELECTION_HIGHLIGHT_ACTIVE_CLASS)
}

const getPageInfo = (): PageInfo => ({
  pagePath: `${window.location.pathname}${window.location.search}`,
  pageUrl: window.location.href,
})

const getDateLabel = (dateValue: string) =>
  new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(dateValue))

const getResponseJson = async <T,>(response: Response): Promise<T | null> => {
  try {
    return await response.json()
  } catch {
    return null
  }
}

const UsernameInput = ({
  inputId,
  username,
  onUsernameChange,
}: UsernameInputProps) => (
  <div className="flex min-w-0 flex-1 items-center gap-1 opacity-50
    transition-opacity focus-within:opacity-90 hover:opacity-75"
  >
    <label
      htmlFor={inputId}
      className="shrink-0 text-[11px] font-semibold text-black/60"
    >
      As
    </label>
    <input
      id={inputId}
      value={username}
      onChange={event => onUsernameChange(event.target.value)}
      placeholder="Enter name"
      className="h-6 min-w-0 max-w-[145px] flex-1 rounded-[3px]
        border border-black/20 bg-white/25 px-1 text-[11px]
        font-medium text-black/75 outline-none transition
        focus:border-black/35 focus:bg-white/60 focus:text-black"
      maxLength={MAX_USERNAME_LENGTH}
    />
  </div>
)

type AutoResizingTextareaProps = ComponentPropsWithoutRef<"textarea"> & {
  value: string
}

const AutoResizingTextarea = ({
  value,
  style,
  ...rest
}: AutoResizingTextareaProps) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  useLayoutEffect(() => {
    const node = textareaRef.current
    if (!node) return
    node.style.height = "auto"
    node.style.height = `${node.scrollHeight}px`
  }, [value])

  return (
    <textarea
      {...rest}
      ref={textareaRef}
      value={value}
      style={{ ...style, overflow: "hidden", resize: "none" }}
    />
  )
}

export const FeedbackWidget = () => {
  const pathname = usePathname()
  // Version swaps change only `?source=`, not the pathname, so pageInfo
  // must track searchParams or feedback is attributed to the wrong version.
  const searchParams = useSearchParams()
  const rootRef = useRef<HTMLDivElement | null>(null)
  const selectionTimeoutRef = useRef<number | null>(null)
  const ignoredSelectionSignatureRef = useRef<string | null>(null)
  const activeSelectionSignatureRef = useRef<string | null>(null)
  // True during a drag-select, so the popover waits for mouseup.
  const isPointerDownRef = useRef(false)
  const [widgetEnabled, setWidgetEnabled] = useState(false)
  const [pageInfo, setPageInfo] = useState<PageInfo | null>(null)
  const [identity, setIdentity] = useState<FeedbackIdentity | null>(null)
  const [lastOpenedAt, setLastOpenedAt] = useState<string | null>(null)
  // When present, we fetch all threads site-wide and show the badge.
  const [reviewerAccessKey, setReviewerAccessKey] = useState<string | null>(null)
  // Epoch-ms of the previous /feedback visit; cutoff for the badge.
  const [reviewerLastVisitMs, setReviewerLastVisitMs] = useState<number | null>(
    null
  )
  // Site-wide threads, kept separate from per-page `threads` so the
  // count is independent of which page the panel shows.
  const [reviewerThreads, setReviewerThreads] = useState<FeedbackThread[]>([])
  const [panelOpen, setPanelOpen] = useState(false)
  const [showAllThreads, setShowAllThreads] = useState(false)
  const [showAllPasswordField, setShowAllPasswordField] = useState(false)
  const [showAllPassword, setShowAllPassword] = useState("")
  const [allThreadsAccessKey, setAllThreadsAccessKey] = useState<string | null>(
    null
  )
  const [showResolvedThreads, setShowResolvedThreads] = useState(false)
  const [selectionDraft, setSelectionDraft] = useState<SelectionDraft | null>(null)
  const [popoverTop, setPopoverTop] = useState<number | null>(null)
  const selectionFormRef = useRef<HTMLFormElement | null>(null)
  const [selectionComment, setSelectionComment] = useState("")
  const [selectionReaction, setSelectionReaction] =
    useState<FeedbackReaction | null>(null)
  // The just-saved thread's id while the dialog fades, so the inline
  // layer holds its icon/underline until it's gone.
  const [closingThreadId, setClosingThreadId] = useState<string | null>(null)
  const [selectionStep, setSelectionStep] = useState<SelectionStep>("comment")
  const [generalComment, setGeneralComment] = useState("")
  const [replyDrafts, setReplyDrafts] = useState<Record<string, string>>({})
  const [threads, setThreads] = useState<FeedbackThread[]>([])
  const [notice, setNotice] = useState<Notice | null>(null)
  const [isLoadingThreads, setIsLoadingThreads] = useState(false)
  const [submittingTarget, setSubmittingTarget] = useState<string | null>(null)

  useLayoutEffect(() => {
    if (!selectionDraft) {
      setPopoverTop(null)
      return
    }

    const formElement = selectionFormRef.current
    if (!formElement) return

    const reposition = () => {
      setPopoverTop(
        computePopoverTop(
          selectionDraft.anchorTop,
          selectionDraft.anchorBottom,
          formElement.offsetHeight,
        ),
      )
    }

    reposition()

    const resizeObserver = new ResizeObserver(reposition)
    resizeObserver.observe(formElement)
    window.addEventListener("resize", reposition)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener("resize", reposition)
    }
  }, [selectionDraft])

  // Drop the highlight when the dialog closes, but keep it through the
  // post-submit fade (closingThreadId) until the underline takes over.
  useEffect(() => {
    if (!selectionDraft && !closingThreadId) clearSelectionHighlight()
  }, [selectionDraft, closingThreadId])

  const lastOpenedStorageKey = useMemo(() => {
    if (!pageInfo) return null
    return getLastOpenedStorageKey(pageInfo.pagePath)
  }, [pageInfo])

  const sortedThreads = useMemo(
    () => [...threads].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
    [threads]
  )

  // Hold the just-submitted thread out of the inline layer while it fades.
  const submittedLayerThreads = useMemo(
    () =>
      closingThreadId
        ? threads.filter(thread => thread.id !== closingThreadId)
        : threads,
    [closingThreadId, threads]
  )

  const unreadCommentCount = useMemo(() => {
    if (!widgetEnabled || !identity) return 0

    const lastOpenedTime = lastOpenedAt ? Date.parse(lastOpenedAt) : 0

    return threads.reduce((count, thread) => {
      if (thread.status !== "active") return count

      const isParticipant = thread.comments.some(
        comment => comment.authorId === identity.id
      )

      if (!isParticipant) return count

      return count + thread.comments.filter(comment => {
        const createdAt = Date.parse(comment.createdAt)
        return comment.authorId !== identity.id && createdAt > lastOpenedTime
      }).length
    }, 0)
  }, [identity, lastOpenedAt, threads, widgetEnabled])

  // Site-wide count of others' comments since the reviewer's last
  // /feedback visit, distinct from the per-page "replies to you" above.
  const reviewerNewCommentCount = useMemo(() => {
    if (!widgetEnabled || !reviewerAccessKey) return 0

    const cutoffMs = reviewerLastVisitMs ?? 0

    return reviewerThreads.reduce((count, thread) => {
      return count + thread.comments.filter(comment => {
        const createdAt = Date.parse(comment.createdAt)
        return (
          comment.authorId !== identity?.id && createdAt > cutoffMs
        )
      }).length
    }, 0)
  }, [
    identity,
    reviewerAccessKey,
    reviewerLastVisitMs,
    reviewerThreads,
    widgetEnabled,
  ])

  useEffect(() => {
    const mediaQueryList = window.matchMedia(FEEDBACK_WIDGET_MEDIA_QUERY)
    const syncWidgetEnabled = () => {
      setWidgetEnabled(mediaQueryList.matches)
    }

    syncWidgetEnabled()
    mediaQueryList.addEventListener("change", syncWidgetEnabled)

    return () => {
      mediaQueryList.removeEventListener("change", syncWidgetEnabled)
    }
  }, [])

  useEffect(() => {
    if (widgetEnabled) return

    setPanelOpen(false)
    setSelectionDraft(null)
  }, [widgetEnabled])

  useEffect(() => {
    setPageInfo(getPageInfo())
  }, [pathname, searchParams])

  useEffect(() => {
    const storedIdentity = getStoredFeedbackIdentity()
    saveFeedbackIdentity(storedIdentity)
    setIdentity(storedIdentity)
  }, [])

  useEffect(() => {
    if (!lastOpenedStorageKey) return

    setLastOpenedAt(window.localStorage.getItem(lastOpenedStorageKey))
  }, [lastOpenedStorageKey])

  // Re-read key + cutoff on every nav: the widget never remounts, so
  // this is how the badge picks up a /feedback password entry or visit.
  useEffect(() => {
    if (!widgetEnabled) return

    setReviewerAccessKey(readStoredAllThreadsAccessKey())
    setReviewerLastVisitMs(readStoredLastVisitMs())
  }, [pathname, panelOpen, widgetEnabled])

  const captureSelection = useCallback(() => {
    if (!widgetEnabled) return
    if (isPointerDownRef.current) return

    const selection = window.getSelection()

    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      ignoredSelectionSignatureRef.current = null
      return
    }

    const selectedText = selection.toString().trim()
    if (selectedText.length < MIN_SELECTED_TEXT_LENGTH) {
      ignoredSelectionSignatureRef.current = null
      return
    }

    const selectionSignature = getSelectionSignature(selection)
    if (selectionSignature === ignoredSelectionSignatureRef.current) return
    // Unchanged selection (e.g. clicking inside the popover): keep the
    // draft and its in-progress reaction/step rather than rebuilding it.
    if (selectionSignature === activeSelectionSignatureRef.current) return

    const range = selection.getRangeAt(0)
    const anchorElement = getNodeElement(selection.anchorNode)
    const focusElement = getNodeElement(selection.focusNode)
    const activeElement = document.activeElement

    if (
      isEditableElement(anchorElement) ||
      isEditableElement(focusElement) ||
      isEditableElement(activeElement) ||
      rootRef.current?.contains(anchorElement) ||
      rootRef.current?.contains(focusElement)
    ) {
      return
    }

    const rect = getRangeRect(range)
    if (rect.width === 0 && rect.height === 0) return

    const width = Math.min(
      POPOVER_MAX_WIDTH,
      window.innerWidth - POPOVER_MARGIN * 2
    )
    if (width < 180) return

    // Anchor right of the selection's block so the dialog sits in the
    // margin, not over text.
    const blockElement = anchorElement?.closest(
      "p, li, blockquote, h1, h2, h3, h4, h5, h6, td, figcaption"
    )
    const columnRight = blockElement
      ? blockElement.getBoundingClientRect().right
      : rect.right
    const maxLeft = Math.max(
      POPOVER_MARGIN,
      window.innerWidth - width - POPOVER_MARGIN
    )
    const left = clamp(
      columnRight + POPOVER_ANCHOR_GAP,
      POPOVER_MARGIN,
      maxLeft
    )
    // Full span (getRangeRect returns only the last line), so the
    // dialog centers on the whole highlight.
    const fullRect = range.getBoundingClientRect()
    const context = getSelectionContext(range, selectedText)

    setSelectionHighlight(range)
    setSelectionDraft({
      selectedText: selectedText.slice(0, MAX_SELECTED_TEXT_LENGTH),
      selectionPrefix: context.selectionPrefix,
      selectionSuffix: context.selectionSuffix,
      left,
      anchorTop: fullRect.top,
      anchorBottom: fullRect.bottom,
      width,
    })
    setSelectionStep("comment")
    setSelectionReaction(null)
    ignoredSelectionSignatureRef.current = null
    activeSelectionSignatureRef.current = selectionSignature
    setNotice(null)
  }, [widgetEnabled])

  const scheduleSelectionCapture = useCallback(() => {
    if (selectionTimeoutRef.current !== null) {
      window.clearTimeout(selectionTimeoutRef.current)
    }

    selectionTimeoutRef.current = window.setTimeout(() => {
      selectionTimeoutRef.current = null
      captureSelection()
    }, 90)
  }, [captureSelection])

  useEffect(() => {
    if (!widgetEnabled) return

    const onPointerDown = () => {
      isPointerDownRef.current = true
    }
    const onPointerUp = () => {
      isPointerDownRef.current = false
      scheduleSelectionCapture()
    }

    document.addEventListener("selectionchange", scheduleSelectionCapture)
    window.addEventListener("mousedown", onPointerDown)
    window.addEventListener("mouseup", onPointerUp)
    window.addEventListener("touchstart", onPointerDown)
    window.addEventListener("touchend", onPointerUp)
    window.addEventListener("keyup", scheduleSelectionCapture)

    return () => {
      document.removeEventListener("selectionchange", scheduleSelectionCapture)
      window.removeEventListener("mousedown", onPointerDown)
      window.removeEventListener("mouseup", onPointerUp)
      window.removeEventListener("touchstart", onPointerDown)
      window.removeEventListener("touchend", onPointerUp)
      window.removeEventListener("keyup", scheduleSelectionCapture)

      if (selectionTimeoutRef.current !== null) {
        window.clearTimeout(selectionTimeoutRef.current)
      }
    }
  }, [scheduleSelectionCapture, widgetEnabled])

  const dismissSelectionDraft = useCallback(() => {
    ignoredSelectionSignatureRef.current = getSelectionSignature(
      window.getSelection()
    )
    activeSelectionSignatureRef.current = null
    setSelectionDraft(null)
    setSelectionStep("comment")
    setSelectionReaction(null)
  }, [])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return

      dismissSelectionDraft()
      setPanelOpen(false)
    }

    const handleScroll = () => {
      dismissSelectionDraft()
    }

    if (!widgetEnabled) return

    window.addEventListener("keydown", handleKeyDown)
    window.addEventListener("scroll", handleScroll, true)

    return () => {
      window.removeEventListener("keydown", handleKeyDown)
      window.removeEventListener("scroll", handleScroll, true)
    }
  }, [dismissSelectionDraft, widgetEnabled])

  const markFeedbackOpened = useCallback(() => {
    if (!lastOpenedStorageKey) return

    const openedAt = new Date().toISOString()
    window.localStorage.setItem(lastOpenedStorageKey, openedAt)
    setLastOpenedAt(openedAt)
  }, [lastOpenedStorageKey])

  useEffect(() => {
    if (!panelOpen) return

    markFeedbackOpened()
  }, [markFeedbackOpened, panelOpen])

  const loadThreads = useCallback(async ({
    showErrors = true,
  }: LoadThreadsOptions = {}) => {
    if (!widgetEnabled || !pageInfo?.pagePath || !identity) return

    setIsLoadingThreads(true)

    try {
      const params = new URLSearchParams({
        pagePath: pageInfo.pagePath,
        participantId: identity.id,
        scope: showAllThreads ? "all" : "mine",
        includeResolved: showResolvedThreads ? "true" : "false",
      })
      const headers = showAllThreads && allThreadsAccessKey
        ? { "x-feedback-access-key": allThreadsAccessKey }
        : undefined
      const response = await fetch(
        `/api/feedback/threads?${params.toString()}`,
        {
          cache: "no-store",
          headers,
        }
      )
      const result = await getResponseJson<ThreadsResponse>(response)

      if (!response.ok) {
        if (response.status === 403) {
          setAllThreadsAccessKey(null)
          setShowAllThreads(false)
          setShowAllPasswordField(true)
        }

        throw new Error(result?.error ?? "Unable to load feedback threads.")
      }

      setThreads(result?.threads ?? [])
      setNotice(null)
    } catch (error) {
      if (showErrors) {
        setNotice({
          tone: "error",
          text: error instanceof Error ? error.message : "Unable to load feedback.",
        })
      }
    } finally {
      setIsLoadingThreads(false)
    }
  }, [
    identity,
    allThreadsAccessKey,
    pageInfo?.pagePath,
    showAllThreads,
    showResolvedThreads,
    widgetEnabled,
  ])

  useEffect(() => {
    if (!widgetEnabled || !identity || !pageInfo) return

    void loadThreads({ showErrors: panelOpen })
  }, [identity, loadThreads, pageInfo, panelOpen, widgetEnabled])

  useEffect(() => {
    if (!widgetEnabled || !identity || !pageInfo) return

    const intervalId = window.setInterval(() => {
      void loadThreads({ showErrors: false })
    }, 60_000)

    return () => window.clearInterval(intervalId)
  }, [identity, loadThreads, pageInfo, widgetEnabled])

  useEffect(() => {
    if (!widgetEnabled || !panelOpen) return
    void loadThreads({ showErrors: true })
  }, [loadThreads, panelOpen, widgetEnabled])

  // Site-wide fetch for the badge, independent of the panel's per-page
  // load. On 403 the stored key is stale, so drop it and hide the badge.
  const loadReviewerThreads = useCallback(async () => {
    if (!widgetEnabled || !reviewerAccessKey) return

    try {
      const params = new URLSearchParams({
        scope: "all",
        includeResolved: "true",
        // Default limit is 50; pull the full set to count accurately.
        limit: "1000",
      })
      const response = await fetch(
        `/api/feedback/threads?${params.toString()}`,
        {
          cache: "no-store",
          headers: { "x-feedback-access-key": reviewerAccessKey },
        }
      )

      if (!response.ok) {
        if (response.status === 403) {
          setReviewerAccessKey(null)
          setReviewerThreads([])
        }
        return
      }

      const result = await getResponseJson<ThreadsResponse>(response)
      setReviewerThreads(result?.threads ?? [])
    } catch {
      // Background refresh — keep the last good count on failure.
    }
  }, [reviewerAccessKey, widgetEnabled])

  useEffect(() => {
    if (!widgetEnabled || !reviewerAccessKey) {
      setReviewerThreads([])
      return
    }

    void loadReviewerThreads()

    const intervalId = window.setInterval(() => {
      void loadReviewerThreads()
    }, REVIEWER_BADGE_POLL_INTERVAL_MS)

    return () => window.clearInterval(intervalId)
  }, [loadReviewerThreads, reviewerAccessKey, widgetEnabled])

  const createThread = useCallback(
    async ({
      commentBody,
      draft,
      target,
    }: {
      commentBody: string
      draft?: SelectionDraft | null
      target: string
    }): Promise<FeedbackThread | null> => {
      if (!pageInfo || !identity) {
        setNotice({
          tone: "error",
          text: "Feedback is still initializing. Please try again.",
        })
        return null
      }

      setSubmittingTarget(target)
      setNotice(null)

      try {
        const response = await fetch("/api/feedback/threads", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            pagePath: pageInfo.pagePath,
            pageUrl: pageInfo.pageUrl,
            selectedText: draft?.selectedText ?? null,
            selectionPrefix: draft?.selectionPrefix ?? null,
            selectionSuffix: draft?.selectionSuffix ?? null,
            commentBody,
            authorId: identity.id,
            authorName: identity.username,
          }),
        })
        const result = await getResponseJson<ThreadResponse>(response)

        if (!response.ok || !result?.thread) {
          throw new Error(result?.error ?? "Unable to save feedback.")
        }

        setThreads(previousThreads => [
          result.thread!,
          ...previousThreads.filter(thread => thread.id !== result.thread!.id),
        ])
        setNotice({ tone: "success", text: "Feedback saved." })
        return result.thread
      } catch (error) {
        setNotice({
          tone: "error",
          text: error instanceof Error ? error.message : "Unable to save feedback.",
        })
        return null
      } finally {
        setSubmittingTarget(null)
      }
    },
    [identity, pageInfo]
  )

  const buildSelectionCommentBody = () => {
    const comment = selectionComment.trim().slice(0, MAX_COMMENT_LENGTH)
    const reactionLabel = selectionReaction
      ? FEEDBACK_REACTION_LABELS[selectionReaction]
      : null

    if (reactionLabel && comment) return `${reactionLabel} — ${comment}`
    if (reactionLabel) return reactionLabel
    return comment
  }

  const selectionHasContent =
    !!selectionReaction || !!selectionComment.trim()

  // An outside click dismisses only an untouched draft; once typed/reacted
  // we keep it open so the click can't discard the feedback.
  useEffect(() => {
    if (!widgetEnabled || !selectionDraft || selectionHasContent) return

    const handlePointerDownOutside = (event: MouseEvent) => {
      if (selectionFormRef.current?.contains(event.target as Node)) return

      dismissSelectionDraft()
    }

    window.addEventListener("mousedown", handlePointerDownOutside)

    return () => {
      window.removeEventListener("mousedown", handlePointerDownOutside)
    }
  }, [dismissSelectionDraft, selectionDraft, selectionHasContent, widgetEnabled])

  const submitSelectionFeedback = async () => {
    const commentBody = buildSelectionCommentBody()
    if (!selectionDraft || !commentBody) return
    if (!identity?.username.trim()) return
    if (submittingTarget === "selection") return

    const savedThread = await createThread({
      commentBody,
      draft: selectionDraft,
      target: "selection",
    })
    if (!savedThread) return

    setClosingThreadId(savedThread.id)
    window.setTimeout(() => {
      resetSelectionDialog()
      setClosingThreadId(null)
    }, SUBMIT_FADE_DURATION_MS)
  }

  const resetSelectionDialog = () => {
    setSelectionComment("")
    setSelectionReaction(null)
    setSelectionStep("comment")
    setSelectionDraft(null)
    activeSelectionSignatureRef.current = null
    window.getSelection()?.removeAllRanges()
  }

  const submitSelectionForm = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!selectionHasContent) return

    // No name yet: divert to the name step before saving.
    if (selectionStep === "comment" && !identity?.username.trim()) {
      setSelectionStep("name")
      return
    }

    void submitSelectionFeedback()
  }

  const submitGeneralComment = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const commentBody = generalComment.trim().slice(0, MAX_COMMENT_LENGTH)
    if (!commentBody) return
    if (!identity?.username.trim()) return
    if (submittingTarget === "general") return

    const wasSaved = await createThread({
      commentBody,
      target: "general",
    })

    if (wasSaved) {
      setGeneralComment("")
    }
  }

  const submitShowAllPassword = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const accessKey = showAllPassword.trim()
    if (!accessKey) return

    setAllThreadsAccessKey(accessKey)
    setShowAllThreads(true)
    setShowAllPassword("")
    setShowAllPasswordField(false)
  }

  const toggleMineOnly = () => {
    setShowAllThreads(false)
    setAllThreadsAccessKey(null)
    setShowAllPassword("")
    setShowAllPasswordField(false)
  }

  const submitReply = async (
    event: FormEvent<HTMLFormElement>,
    threadId: string
  ) => {
    event.preventDefault()

    const commentBody = replyDrafts[threadId]?.trim().slice(0, MAX_COMMENT_LENGTH)
    if (!commentBody || !identity) return
    if (!identity.username.trim()) return
    if (submittingTarget === threadId) return

    setSubmittingTarget(threadId)
    setNotice(null)

    try {
      const response = await fetch(
        `/api/feedback/threads/${threadId}/comments`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            commentBody,
            authorId: identity.id,
            authorName: identity.username,
          }),
        }
      )
      const result = await getResponseJson<ThreadResponse>(response)

      if (!response.ok || !result?.thread) {
        throw new Error(result?.error ?? "Unable to save reply.")
      }

      setThreads(previousThreads => [
        result.thread!,
        ...previousThreads.filter(thread => thread.id !== result.thread!.id),
      ])
      setReplyDrafts(previousDrafts => ({
        ...previousDrafts,
        [threadId]: "",
      }))
      setNotice({ tone: "success", text: "Reply saved." })
      markFeedbackOpened()
    } catch (error) {
      setNotice({
        tone: "error",
        text: error instanceof Error ? error.message : "Unable to save reply.",
      })
    } finally {
      setSubmittingTarget(null)
    }
  }

  const updateThreadStatus = async (
    threadId: string,
    status: FeedbackThread["status"]
  ) => {
    if (!identity) return

    const statusTarget = `status:${threadId}`
    setSubmittingTarget(statusTarget)
    setNotice(null)

    try {
      const response = await fetch(`/api/feedback/threads/${threadId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          status,
          actorId: identity.id,
          actorName: identity.username,
        }),
      })
      const result = await getResponseJson<ThreadResponse>(response)

      if (!response.ok || !result?.thread) {
        throw new Error(result?.error ?? "Unable to update thread.")
      }

      setThreads(previousThreads => {
        if (result.thread!.status === "resolved" && !showResolvedThreads) {
          return previousThreads.filter(thread => thread.id !== threadId)
        }

        return [
          result.thread!,
          ...previousThreads.filter(thread => thread.id !== threadId),
        ]
      })
      setNotice({
        tone: "success",
        text: status === "resolved" ? "Thread resolved." : "Thread reopened.",
      })
      markFeedbackOpened()
    } catch (error) {
      setNotice({
        tone: "error",
        text: error instanceof Error
          ? error.message
          : "Unable to update thread.",
      })
    } finally {
      setSubmittingTarget(null)
    }
  }

  const updateUsername = (username: string) => {
    if (!identity) return

    const nextIdentity = {
      ...identity,
      username: username.slice(0, MAX_USERNAME_LENGTH),
    }
    saveFeedbackIdentity(nextIdentity)
    setIdentity(nextIdentity)
  }

  const removeSavedName = () => {
    updateUsername("")
    setSelectionStep("comment")
  }

  const getCommentAuthorLabel = (comment: FeedbackThread["comments"][number]) => {
    const authorName = comment.authorName ?? "Unknown reader"
    if (!identity || comment.authorId !== identity.id) return authorName

    return `${authorName} (you)`
  }

  const stopWidgetMouseDown = (event: ReactMouseEvent) => {
    event.stopPropagation()
  }

  if (!widgetEnabled) return null

  const hasSavedName = !!identity?.username.trim()
  const isSubmittingSelection = submittingTarget === "selection"
  const selectionPreview = selectionDraft
    ? getSelectionPreview(selectionDraft.selectedText)
    : null

  return (
    <div
      ref={rootRef}
      className="pointer-events-none fixed inset-0 z-[1200] font-sans text-black"
    >
      <SubmittedFeedbackLayer
        threads={submittedLayerThreads}
        enabled={widgetEnabled}
        recomputeKey={pageInfo?.pagePath}
      />

      {selectionDraft && selectionPreview && (
        <form
          ref={selectionFormRef}
          onMouseDown={stopWidgetMouseDown}
          onSubmit={submitSelectionForm}
          className={`fixed overflow-hidden rounded-[6px] border border-black
            bg-[#fffff8] shadow-[0_18px_48px_rgba(0,0,0,0.18)]
            transition-[opacity,transform] duration-200 ease-in ${
              closingThreadId
                ? "pointer-events-none scale-[0.97] opacity-0"
                : "pointer-events-auto"
            }`}
          style={{
            left: selectionDraft.left,
            top: popoverTop ?? selectionDraft.anchorBottom + POPOVER_ANCHOR_GAP,
            width: selectionDraft.width,
            fontFamily: DIALOG_UI_FONT_FAMILY,
          }}
        >
          <div className="flex items-center justify-between gap-2 bg-black px-4 py-2.5
            text-white"
          >
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold leading-none">
                Feedback
              </span>
            </div>

            <div className="flex items-center gap-2">
              {hasSavedName && selectionStep === "comment" && (
                <div className="-translate-x-[5px] flex items-center gap-1.5
                  text-[11px] leading-none"
                >
                  <span className="max-w-[110px] truncate font-medium text-white/60">
                    {identity?.username}
                  </span>
                  <button
                    type="button"
                    onClick={removeSavedName}
                    className="shrink-0 text-white/50 underline underline-offset-2
                      transition hover:text-white/80"
                  >
                    Remove
                  </button>
                </div>
              )}
              <button
                type="button"
                aria-label="Close selected text feedback"
                onClick={dismissSelectionDraft}
                className="grid h-6 w-6 shrink-0 place-items-center rounded-[4px]
                  text-white transition hover:bg-white/25"
              >
                <X size={17} strokeWidth={2.2} />
              </button>
            </div>
          </div>

          <div className="px-4 pb-4 pt-3">
          {selectionStep === "comment" && (
            <p
              className="m-0 truncate text-[15px] leading-snug text-black opacity-60"
              style={{ fontFamily: DIALOG_COPY_FONT_FAMILY }}
            >
              <span className="box-decoration-clone rounded-[2px] bg-[#accef7]
                px-0.5 text-black"
              >
                {selectionPreview.text}
                {selectionPreview.truncated && (
                  <span className="text-black/40"> …</span>
                )}
              </span>
            </p>
          )}

          {notice?.tone === "error" && (
            <div className="mt-3 rounded-[6px] border border-[#8b0000]/30
              bg-[#8b0000]/10 px-2 py-1.5 text-sm leading-snug text-[#5c0000]"
            >
              {notice.text}
            </div>
          )}

          {selectionStep === "comment" ? (
            <>
              <AutoResizingTextarea
                value={selectionComment}
                onChange={event => setSelectionComment(event.target.value)}
                placeholder={SELECTION_COMMENT_PLACEHOLDER}
                className={`mt-[17px] min-h-[88px] w-full rounded-[4px] border border-black
                  bg-white/70 px-3 py-2.5 text-xs leading-snug outline-none
                  transition placeholder:text-black/50 focus:border-2 ${
                    selectionComment.trim() ? "border-2" : "border"
                  }`}
                maxLength={MAX_COMMENT_LENGTH}
              />

              <div className="mt-3">
                <span className="text-[10px] font-medium text-black/45">
                  {SELECTION_REACT_LABEL}
                </span>
                <div className="mt-1.5 flex gap-2">
                {FEEDBACK_REACTION_ORDER.map(reactionKey => {
                  const reactionGlyph = FEEDBACK_REACTION_GLYPHS[reactionKey]
                  const isSelected = selectionReaction === reactionKey
                  return (
                    <button
                      key={reactionKey}
                      type="button"
                      aria-pressed={isSelected}
                      onClick={() =>
                        setSelectionReaction(prev =>
                          prev === reactionKey ? null : reactionKey,
                        )
                      }
                      className={`flex items-center gap-1.5 rounded-[4px] border
                        px-2.5 py-1.5 text-[11px] font-medium leading-none transition ${
                          isSelected
                            ? "border-black bg-black/[0.07] text-black"
                            : "border-black/15 text-black/55 hover:border-black/35 hover:text-black/85"
                        }`}
                    >
                      <span
                        className={`text-[15px] leading-none ${
                          reactionKey === "doesNotResonate"
                            ? "font-medium tracking-tight"
                            : ""
                        }`}
                      >
                        {reactionGlyph}
                      </span>
                      <span>{FEEDBACK_REACTION_LABELS[reactionKey]}</span>
                    </button>
                  )
                })}
                </div>
              </div>

              <button
                type="submit"
                aria-disabled={!selectionHasContent || isSubmittingSelection}
                className={`mt-3 flex h-11 w-full items-center justify-center
                  rounded-[4px] text-sm font-semibold transition ${
                    !selectionHasContent || isSubmittingSelection
                      ? "cursor-not-allowed border border-gray-400 bg-[#fffff8] text-gray-400"
                      : "border-2 border-black bg-[#fffff8] text-black hover:bg-black/5"
                  }`}
              >
                {isSubmittingSelection
                  ? "Saving"
                  : hasSavedName
                    ? "Submit"
                    : "Done"}
              </button>
            </>
          ) : (
            <>
              <div className="mt-3 text-sm text-black/70">
                Who&rsquo;s giving this feedback?
              </div>
              <input
                autoFocus
                value={identity?.username ?? ""}
                onChange={event => updateUsername(event.target.value)}
                placeholder="Your name"
                maxLength={MAX_USERNAME_LENGTH}
                className="mt-2 h-11 w-full rounded-[4px] border border-black/15
                  bg-white/70 px-3 text-sm outline-none transition
                  focus:border-black/45"
              />
              <button
                type="submit"
                aria-disabled={!hasSavedName || isSubmittingSelection}
                className={`mt-3 flex h-11 w-full items-center justify-center
                  rounded-[4px] text-sm font-semibold text-white transition ${
                    !hasSavedName || isSubmittingSelection
                      ? "cursor-not-allowed bg-black/30"
                      : "bg-black hover:bg-black/85"
                  }`}
              >
                {isSubmittingSelection ? "Saving" : "Submit"}
              </button>
            </>
          )}
          </div>
        </form>
      )}

      {panelOpen && (
        <section
          onMouseDown={stopWidgetMouseDown}
          aria-label="Website feedback"
          className="pointer-events-auto fixed bottom-[calc(4.75rem+env(safe-area-inset-bottom))]
            right-3 flex max-h-[min(680px,calc(100vh-6rem))] w-[min(420px,calc(100vw-1.5rem))]
            flex-col overflow-hidden rounded-[8px] border border-black/25 bg-[#fffff8] p-0
            shadow-[0_18px_54px_rgba(0,0,0,0.25)] sm:right-4"
        >
          <header className="flex items-center justify-between border-b border-black/15
            px-4 py-2"
          >
            <div>
              <div className="font-mono text-xs font-bold uppercase tracking-[0.1em]">
                Feedback
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                aria-label="Refresh feedback threads"
                onClick={() => void loadThreads({ showErrors: true })}
                className="grid h-8 w-8 place-items-center rounded-[4px]
                  text-black/65 transition hover:bg-black/10 hover:text-black"
              >
                <RefreshCw
                  size={16}
                  className={isLoadingThreads ? "animate-spin" : undefined}
                />
              </button>
              <button
                type="button"
                aria-label="Close feedback panel"
                onClick={() => setPanelOpen(false)}
                className="grid h-8 w-8 place-items-center rounded-[4px]
                  text-black/65 transition hover:bg-black/10 hover:text-black"
              >
                <X size={18} />
              </button>
            </div>
          </header>

          <div className="min-h-0 overflow-y-auto px-4 py-4">
            {notice && (
              <div
                className={`mb-3 rounded-[4px] border px-3 py-2 text-sm leading-snug ${
                  notice.tone === "error"
                    ? "border-[#8b0000]/30 bg-[#8b0000]/10 text-[#5c0000]"
                    : "border-black/15 bg-black/[0.04] text-black/70"
                }`}
              >
                {notice.text}
              </div>
            )}

            <form onSubmit={submitGeneralComment}>
              <label
                htmlFor="general-feedback"
                className="font-mono text-[11px] font-bold uppercase tracking-[0.08em]"
              >
                General comment
              </label>
              <AutoResizingTextarea
                id="general-feedback"
                value={generalComment}
                onChange={event => setGeneralComment(event.target.value)}
                placeholder="What should we fix?"
                className="mt-2 min-h-24 w-full rounded-[4px] border
                  border-black/20 bg-white/70 px-3 py-2 text-sm leading-snug
                  outline-none transition focus:border-black/55"
                maxLength={MAX_COMMENT_LENGTH}
              />
              <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                <UsernameInput
                  inputId="feedback-username"
                  username={identity?.username ?? ""}
                  onUsernameChange={updateUsername}
                />
                {(() => {
                  const isInactive =
                    !generalComment.trim() ||
                    !identity?.username.trim() ||
                    submittingTarget === "general"
                  return (
                    <Tooltip
                      content={NAME_REQUIRED_TOOLTIP_CONTENT}
                      deactivate={!!identity?.username.trim()}
                      placement="bottom-start"
                      zIndex={1300}
                    >
                      <span className="inline-block">
                        <button
                          type="submit"
                          aria-disabled={isInactive}
                          className={`inline-flex h-9 shrink-0 items-center gap-2
                            rounded-[4px] px-3 text-sm font-semibold text-white
                            transition ${
                              isInactive
                                ? "cursor-not-allowed bg-black/30"
                                : "bg-black hover:bg-black/85"
                            }`}
                        >
                          <Send size={15} />
                          {submittingTarget === "general" ? "Saving" : "Send feedback"}
                        </button>
                      </span>
                    </Tooltip>
                  )
                })()}
              </div>
            </form>

            <div className="mt-5 border-t border-black/15 pt-4">
              <div className="flex items-center justify-between gap-3">
                <div className="font-mono text-[11px] font-bold uppercase tracking-[0.08em]">
                  Threads
                </div>
              </div>

              {isLoadingThreads && sortedThreads.length === 0 ? (
                <div className="mt-3 text-sm text-black/55">Loading threads...</div>
              ) : sortedThreads.length === 0 ? (
                <div className="mt-3 text-sm leading-snug text-black/55">
                  {showAllThreads
                    ? "No feedback threads on this page yet."
                    : "No feedback threads involving you on this page yet."}
                </div>
              ) : (
                <div className="mt-3 space-y-3">
                  {sortedThreads.map(thread => (
                    <article
                      key={thread.id}
                      className={`rounded-[6px] border p-3 ${
                        thread.status === "resolved"
                          ? "border-black/10 bg-white/25 opacity-70"
                          : "border-black/15 bg-white/45"
                      }`}
                    >
                      {thread.selectedText && (
                        <blockquote className="m-0 mb-2 border-l-2 border-[#8b0000]
                          pl-2 text-sm leading-snug text-black/70"
                        >
                          {thread.selectedText}
                        </blockquote>
                      )}
                      <div className="space-y-2">
                        {thread.comments.map((comment, commentIndex) => (
                          <div key={comment.id} className="text-sm leading-snug">
                            <div className="flex items-center justify-between gap-2
                              text-[11px] text-black/45"
                            >
                              <span className="min-w-0">
                                {getCommentAuthorLabel(comment)}
                                {" · "}
                                {getDateLabel(comment.createdAt)}
                              </span>
                              {commentIndex === 0 && (
                                <button
                                  type="button"
                                  aria-label={
                                    thread.status === "resolved"
                                      ? "Reopen feedback thread"
                                      : "Resolve feedback thread"
                                  }
                                  disabled={
                                    submittingTarget === `status:${thread.id}`
                                  }
                                  onClick={() => void updateThreadStatus(
                                    thread.id,
                                    thread.status === "resolved"
                                      ? "active"
                                      : "resolved"
                                  )}
                                  className={`grid h-3.5 w-3.5 shrink-0 place-items-center
                                    rounded-[2px] transition disabled:cursor-not-allowed
                                    disabled:opacity-40 ${
                                      thread.status === "resolved"
                                        ? "text-black/55"
                                        : "text-black/30 hover:text-black/75"
                                    }`}
                                >
                                  {thread.status === "resolved" ? (
                                    <RotateCcw size={12} strokeWidth={2.4} />
                                  ) : (
                                    <Check size={12} strokeWidth={2.4} />
                                  )}
                                </button>
                              )}
                            </div>
                            <div className="whitespace-pre-wrap text-black/80">
                              {comment.body}
                            </div>
                          </div>
                        ))}
                      </div>
                      <form
                        onSubmit={event => void submitReply(event, thread.id)}
                        className="mt-3 flex gap-2"
                      >
                        <input
                          disabled={thread.status === "resolved"}
                          value={replyDrafts[thread.id] ?? ""}
                          onChange={event =>
                            setReplyDrafts(previousDrafts => ({
                              ...previousDrafts,
                              [thread.id]: event.target.value,
                            }))
                          }
                          placeholder={
                            thread.status === "resolved"
                              ? "Reopen to reply"
                              : "Reply"
                          }
                          className="min-w-0 flex-1 rounded-[4px] border
                            border-black/20 bg-white/75 px-2 py-1.5 text-sm
                            outline-none transition focus:border-black/55"
                          maxLength={MAX_COMMENT_LENGTH}
                        />
                        {(() => {
                          const isInactive =
                            thread.status === "resolved" ||
                            !replyDrafts[thread.id]?.trim() ||
                            !identity?.username.trim() ||
                            submittingTarget === thread.id
                          return (
                            <Tooltip
                              content={NAME_REQUIRED_TOOLTIP_CONTENT}
                              deactivate={!!identity?.username.trim()}
                              placement="bottom-start"
                              zIndex={1300}
                            >
                              <span className="inline-block">
                                <button
                                  type="submit"
                                  aria-label="Save reply"
                                  aria-disabled={isInactive}
                                  className={`grid h-8 w-8 shrink-0 place-items-center
                                    rounded-[4px] text-white transition ${
                                      isInactive
                                        ? "cursor-not-allowed bg-black/30"
                                        : "bg-black hover:bg-black/85"
                                    }`}
                                >
                                  <Send size={14} />
                                </button>
                              </span>
                            </Tooltip>
                          )
                        })()}
                      </form>
                    </article>
                  ))}
                </div>
              )}

              <div className="mt-4 flex items-center justify-between gap-3
                border-t border-black/10 pt-3"
              >
                <label className="flex min-w-0 cursor-pointer items-center gap-2
                  text-xs font-medium text-black/55"
                >
                  <input
                    type="checkbox"
                    checked={showResolvedThreads}
                    onChange={event => setShowResolvedThreads(event.target.checked)}
                    className="h-3.5 w-3.5 accent-black"
                  />
                  <span className="truncate">Show resolved threads</span>
                </label>

                {showAllPasswordField ? (
                  <form onSubmit={submitShowAllPassword} className="shrink-0">
                    <input
                      type="password"
                      autoFocus
                      value={showAllPassword}
                      onChange={event => setShowAllPassword(event.target.value)}
                      onBlur={() => {
                        if (!showAllPassword) setShowAllPasswordField(false)
                      }}
                      placeholder="password"
                      className="h-6 w-24 rounded-[3px] border border-black/20
                        bg-white/50 px-1.5 text-xs text-black/70 outline-none
                        transition placeholder:text-black/35 focus:border-black/45
                        focus:bg-white"
                    />
                  </form>
                ) : (
                  <button
                    type="button"
                    onClick={
                      showAllThreads
                        ? toggleMineOnly
                        : () => setShowAllPasswordField(true)
                    }
                    className={`h-6 shrink-0 rounded-[3px] px-2 text-xs
                      font-semibold transition ${
                        showAllThreads
                          ? "bg-black text-white hover:bg-black/85"
                          : "text-black/50 hover:bg-black/10 hover:text-black/75"
                      }`}
                  >
                    {showAllThreads ? "Mine only" : "Show all"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      <div
        className="pointer-events-none fixed bottom-[calc(1rem+env(safe-area-inset-bottom))]
          right-3 inline-flex items-center gap-2 sm:right-4"
      >
        {/* Amber badge: new site-wide comments, for cassandra-key reviewers. */}
        {reviewerNewCommentCount > 0 && (
          <Tooltip content={REVIEWER_BADGE_TOOLTIP}>
            <Link
              href={REVIEWER_BADGE_HREF}
              aria-label={REVIEWER_BADGE_TOOLTIP}
              className="pointer-events-auto grid h-10 min-w-10 place-items-center
                rounded-full bg-amber-400 px-2 text-xs
                font-bold text-black shadow-[0_10px_28px_rgba(0,0,0,0.25)]
                transition hover:bg-amber-300 no-underline"
            >
              {reviewerNewCommentCount > 99 ? "99+" : reviewerNewCommentCount}
            </Link>
          </Tooltip>
        )}

        <button
          type="button"
          aria-label={panelOpen ? "Close feedback" : "Open feedback"}
          onClick={() => setPanelOpen(isOpen => !isOpen)}
          className="pointer-events-auto inline-flex h-12 items-center gap-2
            rounded-full border border-black/20 bg-black px-4 text-sm font-semibold
            text-white shadow-[0_10px_28px_rgba(0,0,0,0.25)] transition
            hover:bg-black/85"
        >
          {panelOpen ? <X size={18} /> : <MessageSquare size={18} />}
          <span>Feedback</span>
          {/* White badge: replies to your threads, this page. */}
          {unreadCommentCount > 0 && (
            <Tooltip content={REPLIES_BADGE_TOOLTIP}>
              <span className="grid h-5 min-w-5 place-items-center rounded-full
                bg-white px-1.5 text-xs font-bold text-black"
              >
                {unreadCommentCount > 99 ? "99+" : unreadCommentCount}
              </span>
            </Tooltip>
          )}
        </button>
      </div>
    </div>
  )
}
